import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getEGiftCardDetail } from "../api/eGiftCard.api";
import type { GetEGiftCardDetailResponse } from "../types/eGiftCard.types";

interface UseGetEGiftCardDetailReturn {
  eGiftCardDetail: GetEGiftCardDetailResponse | null;
  isLoading: boolean;
  error: unknown;
  refetch: () => Promise<GetEGiftCardDetailResponse | null>;
}

export const useGetEGiftCardDetail = (eGiftCardId?: string): UseGetEGiftCardDetailReturn => {
  const [eGiftCardDetail, setEGiftCardDetail] = useState<GetEGiftCardDetailResponse | null>(null);
  const [requestedEGiftCardId, setRequestedEGiftCardId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const { run: runLatestRequest, cancel, loading } = useLatestRequest();

  const fetchEGiftCardDetail = useCallback(async (): Promise<GetEGiftCardDetailResponse | null> => {
    if (!eGiftCardId) return null;

    setRequestedEGiftCardId(eGiftCardId);
    setEGiftCardDetail(null);
    setError(null);

    try {
      const response = await runLatestRequest((signal) => getEGiftCardDetail(eGiftCardId, signal));
      if (!response) return null;

      setEGiftCardDetail(response);
      return response;
    } catch (requestError: unknown) {
      setError(requestError);
      return null;
    }
  }, [eGiftCardId, runLatestRequest]);

  useEffect(() => {
    if (!eGiftCardId) {
      cancel();
      setRequestedEGiftCardId(null);
      setEGiftCardDetail(null);
      setError(null);
      return;
    }

    void fetchEGiftCardDetail();
  }, [cancel, eGiftCardId, fetchEGiftCardDetail]);

  const isCurrentEGiftCard = requestedEGiftCardId === eGiftCardId;

  return {
    eGiftCardDetail: isCurrentEGiftCard ? eGiftCardDetail : null,
    isLoading: Boolean(eGiftCardId) && (!isCurrentEGiftCard || loading),
    error: isCurrentEGiftCard ? error : null,
    refetch: fetchEGiftCardDetail,
  };
};
